import { useEffect, useRef, useState } from 'react'
import { Check, CircleAlert, ShieldCheck, Smartphone, TriangleAlert } from 'lucide-react'
import { AuthShell } from '../auth/AuthShell.js'
import { getSession, logout } from '../auth/api.js'
import { adminMutate, apiRead, ToastProvider } from '../admin/primitives.js'
import { EditingProvider } from '../shell/editing.js'
import { ConfirmProvider } from '../shell/confirm.js'
import {
  CLE_DEMANDE, estVueDemande, heureParis, libellePlateforme, lireDemandeId, messageErreur, origineAdresse,
  phraseAction, retourConnexion, retourRefus, retourSur, type VueDemande,
} from './porte-model.js'

/**
 * « AUTORISER LC PORTE » — /porte/connexion?demande=<id> (22/10/2026).
 *
 * L'app LC Porte ouvre cette page dans une fenêtre d'authentification. La
 * personne se connecte à Gestion (parcours habituel), voit QUEL téléphone et
 * POUR QUOI, puis autorise ou refuse. Gestion se porte garant auprès de la
 * billetterie (côté serveur) et la page repart vers l'app avec le code
 * (`lcporte://connexion?demande=…&code=…`) — jamais affiché ici.
 *
 * Hors du shell (pas de menu) : la carte des écrans de connexion. Les
 * fournisseurs Toast/Confirm/Editing sont montés ici pour que la confirmation
 * d'identité (step-up) s'ouvre d'elle-même quand le serveur la demande.
 */
export function PorteConnexionPage() {
  return (
    <ToastProvider>
      <ConfirmProvider>
        <EditingProvider>
          <AuthShell title="LC Porte" subtitle="Connexion avec Gestion">
            <PorteConnexion />
          </AuthShell>
        </EditingProvider>
      </ConfirmProvider>
    </ToastProvider>
  )
}

type Etape =
  | { e: 'chargement' }
  | { e: 'sans-demande' }
  | { e: 'demande'; vue: VueDemande; erreur: string | null; envoi: 'autoriser' | 'refuser' | null }
  | { e: 'retour'; url: string; autorise: boolean; deconnecte: boolean }
  | { e: 'erreur'; message: string; envoi: boolean; deconnecte?: boolean }

const stockage = (): Storage | null => {
  try { return window.sessionStorage } catch { return null }
}
const oublierDemande = () => {
  try { window.sessionStorage.removeItem(CLE_DEMANDE) } catch { /* stockage indisponible */ }
}

function PorteConnexion() {
  const [etape, setEtape] = useState<Etape>({ e: 'chargement' })
  const idRef = useRef<string | null>(null)

  useEffect(() => {
    const id = lireDemandeId(window.location.search, stockage())
    if (!id) { setEtape({ e: 'sans-demande' }); return }
    idRef.current = id
    void (async () => {
      try {
        const s = await getSession()
        if (!s.authenticated) { window.location.assign(retourConnexion(id)); return }
        const r = await apiRead<unknown>(`/api/v1/porte/connexions/${id}`, { sansRedirection: true })
        if (r.status === 401) { window.location.assign(retourConnexion(id)); return }
        if (r.status === 200 && estVueDemande(r.data)) setEtape({ e: 'demande', vue: r.data, erreur: null, envoi: null })
        else setEtape({ e: 'erreur', message: messageErreur(r.status, r.data), envoi: false })
      } catch {
        setEtape({ e: 'erreur', message: messageErreur(0, null), envoi: false })
      }
    })()
  }, [])

  // Retour vers l'app : un court instant pour lire le résultat, puis la fenêtre repart.
  useEffect(() => {
    if (etape.e !== 'retour') return undefined
    const t = setTimeout(() => { window.location.replace(etape.url) }, 700)
    return () => clearTimeout(t)
  }, [etape])

  const repondre = async (geste: 'autoriser' | 'refuser') => {
    const id = idRef.current
    if (!id || etape.e !== 'demande' || etape.envoi) return
    const club = etape.vue.demande.telephone.proprietaire === 'CLUB'
    // Session déjà fermée par Gestion (téléphone du club, rien à autoriser) : retour direct à l'app.
    if (etape.vue.deconnecte) { oublierDemande(); window.location.replace(retourRefus(id)); return }
    setEtape({ ...etape, envoi: geste, erreur: null })
    try {
      const r = await adminMutate<{ redirect?: unknown; deconnecte?: boolean; error?: string; message?: string }>('POST', `/api/v1/porte/connexions/${id}/${geste}`, undefined, { sansRedirection: true })
      // Session finie : sur un téléphone du club, on revient à l'app (jamais une boucle vers la connexion).
      if (r.status === 401) {
        if (club) { oublierDemande(); window.location.replace(retourRefus(id)) } else window.location.assign(retourConnexion(id))
        return
      }
      const url = r.status === 200 ? retourSur(r.data.redirect, id) : null
      if (url) {
        oublierDemande()
        setEtape({ e: 'retour', url, autorise: geste === 'autoriser', deconnecte: r.data.deconnecte === true })
        return
      }
      // Sur un téléphone du club, Gestion a déjà fermé la session : la page ne peut plus que revenir à l'app.
      if (r.data.deconnecte === true) { setEtape({ e: 'erreur', message: messageErreur(r.status === 200 ? 502 : r.status, r.data), envoi: false, deconnecte: true }); return }
      setEtape({ ...etape, envoi: null, erreur: r.status === 200 ? messageErreur(502, null) : messageErreur(r.status, r.data) })
    } catch {
      setEtape({ ...etape, envoi: null, erreur: messageErreur(0, null) })
    }
  }

  /**
   * Revenir à l'app après une erreur : la demande est refusée (sur un téléphone
   * du club, ou si on ne sait pas, Gestion y ferme la session), puis retour.
   * Gestion injoignable : retour quand même, avec le refus.
   */
  const revenir = async () => {
    const id = idRef.current
    if (!id || etape.e !== 'erreur' || etape.envoi) return
    setEtape({ ...etape, envoi: true })
    let url = retourRefus(id)
    // Session déjà fermée par Gestion : rien à refuser de plus (un appel sans session renverrait vers la connexion).
    if (!etape.deconnecte) {
      try {
        const r = await adminMutate<{ redirect?: unknown; error?: string }>('POST', `/api/v1/porte/connexions/${id}/refuser`, undefined, { sansRedirection: true })
        url = (r.status === 200 ? retourSur(r.data.redirect, id) : null) ?? url
      } catch { /* Gestion injoignable : on revient quand même */ }
    }
    oublierDemande()
    window.location.replace(url)
  }

  const changerDeCompte = async () => {
    const id = idRef.current
    await logout().catch(() => undefined)
    window.location.assign(id ? retourConnexion(id) : '/login')
  }

  return (
    <div className="pt-etape lc-stagger" key={etape.e} aria-live="polite">
      {etape.e === 'chargement' ? (
        <>
          <span className="lc-auth__icon-lg lc-auth__icon-lg--porte"><Smartphone size={30} aria-hidden="true" /></span>
          <p className="lc-auth__body" aria-busy="true">Lecture de la demande de LC Porte…</p>
        </>
      ) : null}

      {etape.e === 'sans-demande' ? (
        <>
          <span className="lc-auth__icon-lg lc-auth__icon-lg--deny"><Smartphone size={30} aria-hidden="true" /></span>
          <p className="lc-auth__body">Cette page s’ouvre depuis l’app LC Porte, quand vous choisissez « Se connecter avec Gestion ». Recommencez depuis l’app.</p>
        </>
      ) : null}

      {etape.e === 'erreur' ? (
        <>
          <span className="lc-auth__icon-lg lc-auth__icon-lg--warn"><TriangleAlert size={30} aria-hidden="true" /></span>
          <p className="lc-auth__body" role="alert">{etape.message}</p>
          <div className="pt-actions">
            <button type="button" className="lc-btn lc-btn--ghost" disabled={etape.envoi} onClick={() => { void revenir() }}>
              {etape.envoi ? 'Retour…' : 'Revenir à LC Porte'}
            </button>
          </div>
        </>
      ) : null}

      {etape.e === 'demande' ? (
        <DemandeVue vue={etape.vue} erreur={etape.erreur} envoi={etape.envoi}
          onAutoriser={() => { void repondre('autoriser') }} onRefuser={() => { void repondre('refuser') }}
          onChangerDeCompte={() => { void changerDeCompte() }} />
      ) : null}

      {etape.e === 'retour' ? (
        <>
          <span className={`lc-auth__icon-lg ${etape.autorise ? 'lc-auth__icon-lg--ok' : 'lc-auth__icon-lg--deny'}`}>
            {etape.autorise ? <Check size={30} aria-hidden="true" /> : <Smartphone size={30} aria-hidden="true" />}
          </span>
          <p className="pt-question">{etape.autorise ? 'C’est autorisé' : 'Demande refusée'}</p>
          <p className="lc-auth__body">
            Retour à l’app LC Porte…{etape.deconnecte ? ' Votre session Gestion est fermée sur ce téléphone.' : ''}
          </p>
          {/* Un bouton, pas un lien : le code ne se lit ni au survol ni à la copie. */}
          <button type="button" className="lc-auth__cta" onClick={() => { window.location.replace(etape.url) }}>Ouvrir LC Porte</button>
        </>
      ) : null}
    </div>
  )
}

/** La demande, telle qu'elle est montrée avant « Autoriser » (présentation seule : testable en rendu statique). */
export function DemandeVue(props: {
  vue: VueDemande
  erreur: string | null
  envoi: 'autoriser' | 'refuser' | null
  onAutoriser: () => void
  onRefuser: () => void
  onChangerDeCompte: () => void
}) {
  const { vue } = props
  const d = vue.demande
  const phrase = phraseAction(vue)
  const plateforme = libellePlateforme(d.telephone.plateforme)
  const origine = origineAdresse(d.adresse_ouverture)
  const detail = [
    d.telephone.proprietaire === 'CLUB' ? 'Téléphone du club' : 'Téléphone personnel',
    plateforme,
    `demande faite à ${heureParis(d.cree_a)}`,
    d.adresse_ouverture ? `depuis ${origine === 'club' ? 'le réseau du club' : 'Internet'} (${d.adresse_ouverture})` : null,
  ].filter(Boolean).join(' · ')
  const occupe = props.envoi !== null

  return (
    <>
      <span className="lc-auth__icon-lg lc-auth__icon-lg--porte"><ShieldCheck size={30} aria-hidden="true" /></span>
      <p className="pt-question">{phrase.question}</p>

      <div className="pt-tel">
        <Smartphone size={22} aria-hidden="true" />
        <div className="pt-tel__texte">
          {/* Le nom vient du téléphone : du texte, isolé (<bdi>), jamais du HTML. */}
          <span className="pt-tel__nom"><bdi>{d.telephone.nom}</bdi></span>
          <span className="pt-tel__detail">{detail}</span>
        </div>
      </div>

      {!vue.peut_autoriser ? (
        <p className="pt-alerte pt-alerte--refus" role="alert">
          <CircleAlert size={16} aria-hidden="true" />
          {vue.raison ?? 'Cette demande ne peut pas être autorisée.'}{vue.deconnecte ? ' Votre session Gestion est fermée sur ce téléphone.' : ''}
        </p>
      ) : (
        <>
          <p className="pt-texte">{phrase.consequence}</p>
          <ul className="pt-droits">
            {vue.moi.droits.map((x) => <li key={x.cle}><Check size={15} aria-hidden="true" />{x.libelle}</li>)}
          </ul>
          {d.mode === 'INSCRIPTION' && origine === 'internet' ? (
            <p className="pt-alerte"><TriangleAlert size={16} aria-hidden="true" />Cette demande vient d’Internet, pas du réseau du club : vérifiez que c’est bien votre téléphone qui vient de la faire.</p>
          ) : null}
          <p className="pt-texte">
            {d.mode === 'INSCRIPTION'
              ? `${vue.authentification.confirmation_requise ? 'Gestion vous demandera d’abord de confirmer votre identité. ' : ''}Une notification vous le confirmera. Si ce n’est pas votre téléphone, refusez.`
              : 'Votre session Gestion sera fermée sur ce téléphone juste après.'}
          </p>
        </>
      )}

      {props.erreur ? <p className="lc-form__error" role="alert">{props.erreur}</p> : null}

      <div className="pt-actions">
        {vue.peut_autoriser ? (
          <button type="button" className="lc-auth__cta" disabled={occupe} onClick={props.onAutoriser}>
            {props.envoi === 'autoriser' ? <span className="lc-spin" aria-hidden="true" /> : null}
            {props.envoi === 'autoriser' ? 'Autorisation…' : 'Autoriser'}
          </button>
        ) : null}
        <button type="button" className="lc-btn lc-btn--ghost" disabled={occupe} onClick={props.onRefuser}>
          {vue.peut_autoriser ? (props.envoi === 'refuser' ? 'Refus…' : 'Refuser') : 'Revenir à LC Porte'}
        </button>
      </div>

      <p className="pt-moi">
        Compte Gestion : {vue.moi.nom} · <button type="button" onClick={props.onChangerDeCompte} disabled={occupe}>Ce n’est pas vous ?</button>
      </p>
    </>
  )
}
